import type { PlanTypes } from "../shared/components/paymentPlans/plans";
import type { User } from "./user";

type UserProperties = {
  subscription_type: PlanTypes;
  full_name: string;
  email: string;
  org_id: number;
  org_name: string;
};

export enum AmplitudePropertyConstraint {
  REQUIRED = "REQUIRED",
  OPTIONAL = "OPTIONAL",
}

export enum AmplitudeEventType {
  PREMIUM_PROMOTION_CLICKED = "Premium Promotion Clicked",
  HIGHLIGHT_CREATED = "Highlight Created",
  LEARNING_PATH_SELECTED = "Learning Path Selected",
  LEARNING_PATH_STARTED = "Learning Path Started",
  LEARNING_PATH_COMPLETED = "Learning Path Completed",
  LESSON_STARTED = "Lesson Started",
  LESSON_COMPLETED = "Lesson Completed",
}

type BaseEventProperties = {
  page_url: string;
};

type MentorProperties = {
  mentor_id: string;
  mentor_name: string;
};

type BaseOneOnOneSessionProperties = BaseEventProperties & MentorProperties;

export type OneOnOneSessionScheduledProperties =
  BaseOneOnOneSessionProperties & {
    schedule_date: Date;
  };

export type OneOnOneSessionReScheduledProperties =
  BaseOneOnOneSessionProperties & {
    reschedule_date: Date;
  };

export type OneOnOneSessionCanceledProperties =
  BaseOneOnOneSessionProperties & {
    cancellation_date: Date;
  };

export type LearningPathSelectedEventProperties = BaseEventProperties & {
  selected_experience: string;
};

export type LearningPathStatusEventProperties = BaseEventProperties & {
  learning_path_id: number;
  learning_path_name: string;
};

export type GamePlanEventProperties = BaseEventProperties & {
  learning_path_name: string;
  course_name: string;
  module_name: string;
  lesson_id: number;
  lesson_name: string;
};

type AnyEventProperties =
  | BaseEventProperties
  | OneOnOneSessionScheduledProperties
  | OneOnOneSessionReScheduledProperties
  | OneOnOneSessionCanceledProperties
  | LearningPathSelectedEventProperties
  | LearningPathStatusEventProperties
  | GamePlanEventProperties;

type Events = {
  [AmplitudeEventType.PREMIUM_PROMOTION_CLICKED]: BaseEventProperties;
  [AmplitudeEventType.LEARNING_PATH_SELECTED]: LearningPathSelectedEventProperties;
  [AmplitudeEventType.LEARNING_PATH_STARTED]: LearningPathStatusEventProperties;
  [AmplitudeEventType.LEARNING_PATH_COMPLETED]: LearningPathStatusEventProperties;
  [AmplitudeEventType.HIGHLIGHT_CREATED]: GamePlanEventProperties;
  [AmplitudeEventType.LESSON_STARTED]: GamePlanEventProperties;
  [AmplitudeEventType.LESSON_COMPLETED]: GamePlanEventProperties;
};

type AmplitudeUserProperties = {
  user_id: User["userProfileId"];
  user_properties?: Partial<UserProperties>;
};

type AmplitudeEventProperties<
  T = unknown,
  Constraint = AmplitudePropertyConstraint.REQUIRED
> = {
  event_properties: T extends AnyEventProperties
    ? Constraint extends AmplitudePropertyConstraint.REQUIRED
      ? T
      : Partial<T>
    : T extends AmplitudeEventType
    ? Constraint extends AmplitudePropertyConstraint.REQUIRED
      ? Events[T]
      : Partial<Events[T]>
    : never;
};

export type AmplitudeEvent<
  T = unknown,
  K = AmplitudePropertyConstraint.REQUIRED
> = T extends AmplitudeEventType | AnyEventProperties
  ?
      | AmplitudeEventProperties<T, K>
      | (AmplitudeUserProperties & AmplitudeEventProperties<T, K>)
  : never;

export type AmplitudeTrack<T = unknown> = T extends AmplitudeEventType
  ? AmplitudeEvent<T> & {
      event_type: T;
    }
  : never;
